import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ticketsAPI, contactsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Search, ArrowLeft, Send, Lock, X } from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_MAP = {
  urgent: { label: 'Urgent', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  high:   { label: 'High',   cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  medium: { label: 'Medium', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  low:    { label: 'Low',    cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
};

const STATUS_MAP = {
  open:        { label: 'Ouvert',       cls: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'En cours',     cls: 'bg-violet-100 text-violet-700' },
  resolved:    { label: 'Résolu',       cls: 'bg-emerald-100 text-emerald-700' },
  closed:      { label: 'Fermé',        cls: 'bg-slate-100 text-slate-600' },
};

function PriorityBadge({ priority }) {
  const p = PRIORITY_MAP[priority] || PRIORITY_MAP.medium;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.cls}`}>{p.label}</span>;
}

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.open;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + ' ' + dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function TicketListItem({ ticket, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-colors ${selected ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/50'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <PriorityBadge priority={ticket.priority} />
            <StatusBadge status={ticket.status} />
          </div>
          <p className="font-semibold text-sm text-foreground truncate">{ticket.title}</p>
          {ticket.contact && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {ticket.contact.first_name} {ticket.contact.last_name}
            </p>
          )}
        </div>
        <p className="text-xs text-muted-foreground flex-shrink-0">{fmtDate(ticket.created_at)}</p>
      </div>
      <p className="text-xs font-mono text-muted-foreground/70 mt-1">{ticket.ticket_number}</p>
    </button>
  );
}

function MessageBubble({ msg }) {
  if (msg.is_internal) {
    return (
      <div className="mx-2 my-1">
        <div className="bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-700/50 rounded-lg px-3 py-2">
          <div className="flex items-center gap-1.5 mb-1">
            <Lock size={11} className="text-amber-600" />
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">Note interne</span>
            <span className="text-xs text-muted-foreground ml-auto">{msg.author_name} · {fmtDate(msg.created_at)}</span>
          </div>
          <p className="text-sm text-foreground">{msg.content}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-2 my-1 flex flex-col">
      <div className="max-w-[80%] self-end">
        <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-3 py-2">
          <p className="text-sm">{msg.content}</p>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 text-right">{msg.author_name} · {fmtDate(msg.created_at)}</p>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'all', label: 'Tous' },
  { id: 'open', label: 'Ouverts' },
  { id: 'in_progress', label: 'En cours' },
  { id: 'urgent', label: 'Urgents' },
  { id: 'closed', label: 'Fermés' },
];

const EMPTY_FORM = { title: '', description: '', priority: 'medium', status: 'open', contact_id: '' };

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'detail'

  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);

  const messagesEndRef = useRef(null);

  const loadTickets = useCallback(async () => {
    try {
      const params = {};
      if (activeTab === 'urgent') params.priority = 'urgent';
      else if (activeTab === 'closed') params.status = 'closed';
      else if (activeTab === 'in_progress') params.status = 'in_progress';
      else if (activeTab === 'open') params.status = 'open';
      const res = await ticketsAPI.list(params);
      setTickets(res.data);
      // Update selected ticket data if we have one
      if (selectedTicket) {
        const updated = res.data.find(t => t.id === selectedTicket.id);
        if (updated) setSelectedTicket(updated);
      }
    } catch {
      toast.error('Erreur lors du chargement des tickets');
    } finally {
      setLoading(false);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadTickets(); }, [loadTickets]);

  useEffect(() => {
    contactsAPI.list().then(res => setContacts(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket?.messages]);

  const filtered = tickets.filter(t =>
    !search ||
    t.title.toLowerCase().includes(search.toLowerCase()) ||
    t.ticket_number.toLowerCase().includes(search.toLowerCase())
  );

  const selectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setMobileView('detail');
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    setSendingMsg(true);
    try {
      await ticketsAPI.addMessage(selectedTicket.id, {
        content: newMessage.trim(),
        is_internal: isInternal,
      });
      setNewMessage('');
      setIsInternal(false);
      // Refresh ticket detail
      const res = await ticketsAPI.get(selectedTicket.id);
      setSelectedTicket(res.data);
    } catch {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingMsg(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    try {
      const res = await ticketsAPI.update(selectedTicket.id, { status: newStatus });
      setSelectedTicket(res.data);
      loadTickets();
      toast.success('Statut mis à jour');
    } catch {
      toast.error('Erreur');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        contact_id: form.contact_id || null,
      };
      await ticketsAPI.create(data);
      toast.success('Ticket créé');
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      loadTickets();
    } catch {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // --- Mobile: detail view ---
  if (mobileView === 'detail' && selectedTicket) {
    return (
      <div className="md:hidden flex flex-col h-[calc(100vh-8rem)] animate-fade-in">
        {/* Mobile header */}
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <button onClick={() => setMobileView('list')} className="p-1.5 rounded-md hover:bg-muted">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-foreground truncate">{selectedTicket.title}</p>
            <p className="text-xs font-mono text-muted-foreground">{selectedTicket.ticket_number}</p>
          </div>
          <Select value={selectedTicket.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_MAP).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <TicketDetail
          ticket={selectedTicket}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          isInternal={isInternal}
          setIsInternal={setIsInternal}
          onSend={handleSendMessage}
          sending={sendingMsg}
          messagesEndRef={messagesEndRef}
          onStatusChange={handleStatusChange}
        />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Support Tickets</h1>
          <p className="text-sm text-muted-foreground">{tickets.length} ticket{tickets.length > 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} size="sm" className="gap-2">
          <Plus size={15} />
          Nouveau ticket
        </Button>
      </div>

      {/* Desktop split view */}
      <div className="hidden md:flex gap-0 border border-border rounded-xl overflow-hidden bg-card" style={{ height: 'calc(100vh - 14rem)' }}>
        {/* List panel */}
        <div className="w-80 flex-shrink-0 flex flex-col border-r border-border">
          {/* Tabs */}
          <div className="flex border-b border-border overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2.5 text-xs font-medium whitespace-nowrap flex-shrink-0 border-b-2 transition-colors ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>
          {/* Ticket list */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {loading ? (
              [...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)
            ) : filtered.length === 0 ? (
              <p className="text-center py-8 text-sm text-muted-foreground">Aucun ticket</p>
            ) : (
              filtered.map(t => (
                <TicketListItem
                  key={t.id}
                  ticket={t}
                  selected={selectedTicket?.id === t.id}
                  onClick={() => setSelectedTicket(t)}
                />
              ))
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div className="flex-1 flex flex-col min-w-0">
          {selectedTicket ? (
            <>
              {/* Detail header */}
              <div className="px-4 py-3 border-b border-border flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">{selectedTicket.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs font-mono text-muted-foreground">{selectedTicket.ticket_number}</span>
                    <PriorityBadge priority={selectedTicket.priority} />
                  </div>
                </div>
                <Select value={selectedTicket.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-36 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_MAP).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTicket.status !== 'closed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange('closed')}
                  >
                    Fermer le ticket
                  </Button>
                )}
              </div>

              <TicketDetail
                ticket={selectedTicket}
                newMessage={newMessage}
                setNewMessage={setNewMessage}
                isInternal={isInternal}
                setIsInternal={setIsInternal}
                onSend={handleSendMessage}
                sending={sendingMsg}
                messagesEndRef={messagesEndRef}
                onStatusChange={handleStatusChange}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <p className="text-sm">Sélectionnez un ticket</p>
                <p className="text-xs mt-1">pour voir le détail et les messages</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: list view */}
      <div className="md:hidden space-y-2">
        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap flex-shrink-0 transition-colors ${activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Search */}
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-sm" />
        </div>
        {/* List */}
        {loading ? (
          [...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
        ) : filtered.length === 0 ? (
          <p className="text-center py-10 text-sm text-muted-foreground">Aucun ticket</p>
        ) : (
          <div className="bg-card border border-border rounded-xl divide-y divide-border overflow-hidden">
            {filtered.map(t => (
              <TicketListItem key={t.id} ticket={t} selected={false} onClick={() => selectTicket(t)} />
            ))}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau ticket</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input value={form.title} onChange={e => setField('title', e.target.value)} required />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={form.description} onChange={e => setField('description', e.target.value)} rows={3} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priorité</Label>
                <Select value={form.priority} onValueChange={v => setField('priority', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={form.status} onValueChange={v => setField('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Ouvert</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Contact lié</Label>
              <Select value={form.contact_id || 'none'} onValueChange={v => setField('contact_id', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {contacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Création...' : 'Créer le ticket'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TicketDetail({ ticket, newMessage, setNewMessage, isInternal, setIsInternal, onSend, sending, messagesEndRef }) {
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Description */}
      {ticket.description && (
        <div className="px-4 py-3 bg-muted/30 border-b border-border">
          <p className="text-xs text-muted-foreground">{ticket.description}</p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-3 space-y-1">
        {ticket.messages?.length === 0 ? (
          <p className="text-center py-8 text-sm text-muted-foreground">Aucun message</p>
        ) : (
          ticket.messages?.map(msg => (
            <MessageBubble key={msg.id} msg={msg} />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply box */}
      <div className="border-t border-border p-3 space-y-2">
        <Textarea
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          placeholder={isInternal ? 'Note interne (visible uniquement par l\'équipe)...' : 'Répondre au client...'}
          rows={2}
          className={isInternal ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-700/50' : ''}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onSend();
          }}
        />
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isInternal}
              onChange={e => setIsInternal(e.target.checked)}
              className="rounded"
            />
            <Lock size={11} className={isInternal ? 'text-amber-600' : ''} />
            <span className={isInternal ? 'text-amber-700 font-medium' : ''}>Note interne</span>
          </label>
          <Button
            size="sm"
            onClick={onSend}
            disabled={!newMessage.trim() || sending}
            className="gap-1.5"
          >
            <Send size={13} />
            {sending ? 'Envoi...' : 'Envoyer'}
          </Button>
        </div>
      </div>
    </div>
  );
}
